/**
 * Watched factions, the recruitment scanner and faction transfers (website roadmap S24 — Torn Intel Pro's watch list,
 * recruitment and transfers; plus S22's leftover: the server-key watch of factions Omni-Torn users follow). One copy for the
 * server (apps/api/src/faction-watch.ts, faction-moves.ts, player-db.ts `recruit`) and the website
 * (apps/web/src/app/pages/factions-scout.ts). Pure, browser-safe.
 *
 * - Watch list: a faction's officers (leader, co-leader, members whose key has the faction's API access) pick up to
 *   WATCH_MAX other factions; every member of that faction sees the list. The server reads each watched faction's public
 *   member list (Torn v2 `/faction/{id}/members`) with lent keys first, else its own key — WATCH_CALLS_PER_MINUTE calls a
 *   minute for every watch list together, each faction about every WATCH_EVERY_MINUTES — and keeps counts per read
 *   (WATCH_KEEP_DAYS) for the patterns, the members' activity in the activity store (S22) and their faction in the player
 *   database (for transfers). Only lists an officer on FACTION_WATCH_TERMS made are read.
 * - "Stacking before a war": `stackingAlert` — members coming back from travel or hospital, more active than usual, fewer
 *   abroad than a while ago, a ranked war about to start.
 * - Transfers (TRANSFERS_TERMS): who left which faction and where they went — from the player database's faction changes
 *   (only from what senders on TRANSFERS_TERMS saw, or the server's own reads) and the watch's roster changes.
 * - Recruitment: the player database searched by estimate band, level, activity, company (only what senders on
 *   RECRUIT_COMPANY_TERMS saw), factionless — estimates as everywhere else, never anyone's own stats.
 */

import { readTravel } from './travel';
import { placeOf } from './war-room';
import type { FactionRosterMember } from './faction-profile';

// ---------------------------------------------------------------- terms and limits

/** From this terms version on: an officer may make a watch list (read with lent/server keys, shown to the faction's members). */
export const FACTION_WATCH_TERMS = 11;
/** From this terms version on: faction moves a sender saw are kept and shown to Omni-Torn users (Transfers). */
export const TRANSFERS_TERMS = 11;
/** From this terms version on: the company a sender saw a player in may be searched and shown in Recruitment. */
export const RECRUIT_COMPANY_TERMS = 11;

/** Factions on one faction's watch list at most. */
export const WATCH_MAX = 30;
/** Each watched faction is read about this often (when the budget allows). */
export const WATCH_EVERY_MINUTES = 15;
/** Torn calls a minute for every watch list together (lent keys first, else the server's own key — its 15 a minute are shared). */
export const WATCH_CALLS_PER_MINUTE = 2;
/** A faction's list is read only while one of its members looked at it within this many days. */
export const WATCH_IDLE_DAYS = 7;
/** Counts per read kept this long (the patterns). */
export const WATCH_KEEP_DAYS = 30;
/** Moves kept this long. */
export const TRANSFERS_KEEP_DAYS = 365;
/** GET /api/transfers answers at most this many moves. */
export const TRANSFERS_MAX = 500;
/** Recruitment answers at most this many players. */
export const RECRUIT_MAX = 100;
/** Players weighed for one recruitment search (activity is worked out for these). */
export const RECRUIT_POOL = 200;
/** "Coming back": hospital or jail ending, or flying home, within this long. */
export const WATCH_BACK_SOON_MS = 60 * 60_000;
/** Active "now": a last action within this long. */
export const WATCH_ACTIVE_MS = 15 * 60_000;

export const WATCH_PATH = '/api/faction/watch';
export const TRANSFERS_PATH = '/api/transfers';
export const RECRUIT_PATH = '/api/recruit';

// ---------------------------------------------------------------- one read of a watched faction

/** What one read of a faction's members comes to (counts only — nothing about one player). */
export interface WatchSnapshot {
  /** When it was read (ms). */
  at: number;
  members: number;
  /** Last action within WATCH_ACTIVE_MS. */
  active: number;
  /** Torn's Online / Idle now. */
  online: number;
  idle: number;
  okay: number;
  hospital: number;
  jail: number;
  /** Flying either way. */
  flying: number;
  abroad: number;
  /** Coming back within WATCH_BACK_SOON_MS: hospital or jail ending, or flying home. */
  returning: number;
}

/** A roster (Torn's public member list) → the counts. */
export function watchSnapshot(roster: readonly FactionRosterMember[], at: number): WatchSnapshot {
  const s: WatchSnapshot = { at, members: roster.length, active: 0, online: 0, idle: 0, okay: 0, hospital: 0, jail: 0, flying: 0, abroad: 0, returning: 0 };
  for (const m of roster) {
    if (m.lastAction !== null && at - m.lastAction <= WATCH_ACTIVE_MS) s.active++;
    if (m.activity === 'online') s.online++;
    else if (m.activity === 'idle') s.idle++;
    if (comingBack(m, at)) s.returning++;
    if (/^hospital$/i.test(m.state)) s.hospital++;
    else if (/^jail$/i.test(m.state)) s.jail++;
    else {
      const p = placeOf(m).where;
      if (p === 'flying') s.flying++;
      else if (p === 'abroad') s.abroad++;
      else if (/^okay$/i.test(m.state)) s.okay++;
    }
  }
  return s;
}

/** Coming back within the hour: hospital or jail ending, or flying home (Torn's "Returning to Torn from …"). */
export function comingBack(m: Pick<FactionRosterMember, 'state' | 'description' | 'until'>, now: number): boolean {
  if (/^(hospital|jail)$/i.test(m.state)) return !!m.until && m.until > now && m.until - now <= WATCH_BACK_SOON_MS;
  return /^(traveling|travelling)$/i.test(m.state) && readTravel(m.description ?? '')?.leg === 'back';
}

// ---------------------------------------------------------------- "stacking before a war"

export type StackingLevel = 'quiet' | 'watch' | 'stacking';

export interface StackingAlert {
  level: StackingLevel;
  /** Plain reasons, most telling first. */
  reasons: string[];
}

/** A ranked war of the watched faction (from Torn's kept ranked war list): when it starts and against whom. */
export interface WatchWar {
  /** Unix s. */
  start: number;
  end: number | null;
  enemyId: number;
  enemyName: string;
}

const agoText = (ms: number): string => (ms >= 3_600_000 ? `${Math.round(ms / 3_600_000)} h` : `${Math.max(1, Math.round(ms / 60_000))} min`);

/**
 * Is the faction getting ready (stacking) — members coming back from travel or hospital, more of them active than
 * usual, fewer abroad than a couple of hours ago, a ranked war about to start? `history` = earlier reads (any order),
 * `usualActive` = how many are usually active at this time of day (the activity store's last week), when known.
 * Two signs (or one with a war within 24 hours) = 'stacking'; one = 'watch'.
 */
export function stackingAlert(now: WatchSnapshot, history: readonly WatchSnapshot[], opts: { usualActive?: number | null; war?: WatchWar | null } = {}): StackingAlert {
  const reasons: string[] = [];
  let signs = 0;
  const big = Math.max(3, Math.ceil(now.members * 0.1));
  if (now.returning >= big) {
    signs++;
    reasons.push(`${now.returning} coming back from travel or hospital within the hour`);
  }
  // Usual: the activity store's same time of day, else the reads of the last day (older than an hour).
  const earlier = history.filter((h) => h.at < now.at - 3_600_000 && h.at >= now.at - 86_400_000);
  const usual = opts.usualActive ?? (earlier.length >= 4 ? earlier.reduce((a, h) => a + h.active, 0) / earlier.length : null);
  if (usual !== null && now.active >= big && now.active >= usual * 1.5 + 2) {
    signs++;
    reasons.push(`${now.active} active in the last 15 minutes (usually about ${Math.round(usual)})`);
  }
  // Fewer away (flying or abroad) than 1–3 hours ago.
  const before = history.filter((h) => h.at <= now.at - 3_600_000 && h.at >= now.at - 3 * 3_600_000).sort((a, b) => a.at - b.at)[0];
  if (before) {
    const awayThen = before.abroad + before.flying;
    const awayNow = now.abroad + now.flying;
    if (awayThen - awayNow >= big) {
      signs++;
      reasons.push(`${awayThen - awayNow} fewer away (flying or abroad) than ${agoText(now.at - before.at)} ago`);
    }
  }
  const w = opts.war;
  const nowS = now.at / 1000;
  const warSoon = !!w && w.start > nowS && w.start - nowS <= 86_400;
  const warOn = !!w && w.start <= nowS && (w.end === null || w.end > nowS);
  if (w && warSoon) reasons.unshift(`ranked war against ${w.enemyName} starts in ${agoText((w.start - nowS) * 1000)}`);
  else if (w && warOn) reasons.unshift(`in a ranked war against ${w.enemyName}`);
  const level: StackingLevel = signs >= 2 || (signs >= 1 && warSoon) ? 'stacking' : signs === 1 || warSoon ? 'watch' : 'quiet';
  return { level, reasons };
}

/** The usual number of members active at this time of day: the same 15 minutes on earlier days of a faction series. */
export function usualActiveAt(series: { from: number; bucketMin: number; active: number[] }, at: number): number | null {
  const step = series.bucketMin * 60_000;
  const day = 86_400_000;
  const vals: number[] = [];
  for (let t = at - day; t >= series.from; t -= day) {
    const i = Math.floor((t - series.from) / step);
    if (i >= 0 && i < series.active.length) vals.push(series.active[i]!);
  }
  return vals.length >= 3 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

// ---------------------------------------------------------------- the watch list's answers

export interface WatchedFactionView {
  id: number;
  name: string | null;
  tag: string | null;
  /** When it was put on the list (ms). */
  addedAt: number;
  /** The newest read (null: not read yet). */
  now: WatchSnapshot | null;
  alert: StackingAlert | null;
  war: WatchWar | null;
  /** Why it couldn't be read lately (null: fine). */
  problem: string | null;
  /** When the next read is due (ms; null: the list isn't read — see `paused`). */
  next: number | null;
}

/** GET /api/faction/watch — your faction's watch list (members only). */
export interface FactionWatchAnswer {
  factionId: number;
  /** May you add and remove (leader, co-leader, a key with the faction's API access)? */
  officer: boolean;
  /** Have you agreed to FACTION_WATCH_TERMS (adding needs it)? */
  agreed: boolean;
  terms: number;
  max: number;
  everyMinutes: number;
  /** The server can read Torn for the watch (a lent key or its own key). */
  readable: boolean;
  factions: WatchedFactionView[];
}

/** GET /api/faction/watch/:id — one watched faction's reads over the last days (the patterns). */
export interface WatchHistoryAnswer {
  id: number;
  days: number;
  reads: WatchSnapshot[];
}

/** POST /api/faction/watch body. */
export interface WatchAddBody {
  factionId: number;
}

// ---------------------------------------------------------------- transfers

/** One faction move: a player seen in `from`, later not in it — and, when known, in `to` (0 = in no faction). */
export interface FactionMove {
  playerId: number;
  /** The faction left (null: not known — only seen joining). */
  from: number | null;
  /** The faction joined (0: no faction; null: not seen anywhere yet). */
  to: number | null;
  /** Last seen in `from` (ms; null: not known). */
  leftAfter: number | null;
  /** First seen not in `from` / in `to` (ms). */
  at: number;
}

/** A move with what the player database knows of the player (public facts and Omni-Torn's estimate). */
export interface FactionMoveRow extends FactionMove {
  name: string | null;
  level: number | null;
  total: number | null;
  totalSource: string | null;
  totalAt: number | null;
}

export type TransferDirection = 'left' | 'joined' | 'both';

/** GET /api/transfers answers. */
export interface TransfersAnswer {
  at: number;
  /** The faction asked about (null: every faction). */
  factionId: number | null;
  days: number;
  direction: TransferDirection;
  terms: number;
  moves: FactionMoveRow[];
  /** Faction names Omni-Torn knows (id → name). */
  factions: Record<string, string>;
  /** More moves than shown. */
  more: boolean;
}

/** One arrow of the flow chart: how many went from one faction to another. */
export interface TransferFlow {
  from: number | null;
  to: number | null;
  n: number;
}

/** The moves added up, biggest first (at most `max`). */
export function transferFlows(moves: readonly FactionMove[], max = 20): TransferFlow[] {
  const by = new Map<string, TransferFlow>();
  for (const m of moves) {
    const k = `${m.from ?? 'x'}>${m.to ?? 'x'}`;
    const f = by.get(k);
    if (f) f.n++;
    else by.set(k, { from: m.from, to: m.to, n: 1 });
  }
  return [...by.values()].sort((a, b) => b.n - a.n || (a.from ?? 0) - (b.from ?? 0) || (a.to ?? 0) - (b.to ?? 0)).slice(0, max);
}

/** For one faction: left (and where to), joined (and where from), and the net. */
export function transferTotals(moves: readonly FactionMove[], factionId: number): { left: number; joined: number; net: number } {
  let left = 0;
  let joined = 0;
  for (const m of moves) {
    if (m.from === factionId && m.to !== factionId) left++;
    if (m.to === factionId && m.from !== factionId) joined++;
  }
  return { left, joined, net: joined - left };
}

export const isTransferDirection = (x: unknown): x is TransferDirection => x === 'left' || x === 'joined' || x === 'both';

// ---------------------------------------------------------------- recruitment

export type RecruitFaction = 'none' | 'any' | 'some';
export type RecruitCompany = 'any' | 'none' | 'some';
export type RecruitSort = 'active' | 'estimate' | 'level';

/** POST /api/recruit body (every field optional). */
export interface RecruitQuery {
  totalMin?: number | null;
  totalMax?: number | null;
  /** Players with no estimate yet too (with a band). */
  includeUnknown?: boolean;
  levelMin?: number | null;
  levelMax?: number | null;
  /** Factionless (default), any, or in a faction. */
  faction?: RecruitFaction;
  company?: RecruitCompany;
  /** Last action within this many days (1–365; default 7). */
  seenDays?: number | null;
  /** At least this many active hours a day over the last 30 days (Omni-Torn's activity tracker; 0 = any). */
  hoursMin?: number | null;
  sort?: RecruitSort;
  limit?: number;
}

export interface RecruitRow {
  id: number;
  name: string | null;
  level: number | null;
  factionId: number | null;
  total: number | null;
  totalSource: string | null;
  totalAt: number | null;
  lastAction: number | null;
  /** The company seen (only what senders on RECRUIT_COMPANY_TERMS saw): null when not known. */
  company: { id: number; name: string | null; type: number | null } | null;
  /** Active hours a day over the last 30 days (null: not seen enough). */
  hoursPerDay: number | null;
  activeDays: number;
}

export interface RecruitAnswer {
  at: number;
  rows: RecruitRow[];
  /** Players matching before the activity filter. */
  weighed: number;
  more: boolean;
  terms: number;
}

/** A recruitment query cleaned (numbers in range, defaults). */
export function cleanRecruitQuery(q: unknown): Required<Omit<RecruitQuery, 'totalMin' | 'totalMax' | 'levelMin' | 'levelMax' | 'hoursMin'>> & Pick<RecruitQuery, 'totalMin' | 'totalMax' | 'levelMin' | 'levelMax' | 'hoursMin'> {
  const o = (q && typeof q === 'object' ? q : {}) as Record<string, unknown>;
  const n = (x: unknown, lo: number, hi: number): number | null => (typeof x === 'number' && Number.isFinite(x) && x >= lo && x <= hi ? x : null);
  const faction: RecruitFaction = o.faction === 'any' || o.faction === 'some' ? o.faction : 'none';
  const company: RecruitCompany = o.company === 'none' || o.company === 'some' ? o.company : 'any';
  const sort: RecruitSort = o.sort === 'estimate' || o.sort === 'level' ? o.sort : 'active';
  return {
    totalMin: n(o.totalMin, 0, 1e16),
    totalMax: n(o.totalMax, 0, 1e16),
    includeUnknown: o.includeUnknown === true,
    levelMin: n(o.levelMin, 1, 100),
    levelMax: n(o.levelMax, 1, 100),
    faction,
    company,
    seenDays: n(o.seenDays, 1, 365) ?? 7,
    hoursMin: n(o.hoursMin, 0, 24),
    sort,
    limit: Math.max(1, Math.min(RECRUIT_MAX, Math.floor(n(o.limit, 1, RECRUIT_MAX) ?? 50))),
  };
}

/** "1.5b", "200m", "40k", "1,000,000" → a number (null: not one). */
export function parseStatAmount(raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(/[,\s_]/g, '');
  if (!s) return null;
  const m = /^(\d+(?:\.\d+)?)([kmbt]?)$/.exec(s);
  if (!m) return null;
  const mult = { '': 1, k: 1e3, m: 1e6, b: 1e9, t: 1e12 }[m[2] as '' | 'k' | 'm' | 'b' | 't'];
  const v = Number(m[1]) * mult;
  return Number.isFinite(v) ? Math.round(v) : null;
}
